import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from './useAuth';

// Context für Features
const FeaturesContext = createContext(null);

/**
 * Features Provider - Stellt Features und Usage-Daten für die gesamte App bereit
 */
export function FeaturesProvider({ children }) {
  const [features, setFeatures] = useState({});
  const [usage, setUsage] = useState({});
  const [role, setRole] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Gefragt wird nur mit einer Sitzung, die `/me` bestätigt hat (seit
  // 06.10.2026). Vorher fragte der Provider blind und nahm die 401 eines
  // Gastes (Registrierung, Preisseite) als „erwartet" hin — jeder Gast hatte
  // damit einen roten Eintrag in der Konsole, und eine 401 aus einem echten
  // Grund sah genauso aus. Ohne Sitzung gibt es keine Merkmale; das ist ein
  // Zustand, keine Antwort.
  const { sitzungsNutzer, loading: authLaedt } = useAuth();
  const nutzerId = sitzungsNutzer?.id ?? null;
  const fuerNutzer = useRef(nutzerId);
  fuerNutzer.current = nutzerId;

  const loadFeatures = useCallback(async () => {
    const id = fuerNutzer.current;
    if (id === null) return;
    try {
      // Der Token lebt im HttpOnly-Cookie, der Browser schickt ihn mit.
      const response = await authAPI.getFeatures();
      if (fuerNutzer.current !== id) return;
      setFeatures(response.data.features || {});
      setUsage(response.data.usage || {});
      setRole(response.data.role);
      setRoleName(response.data.role_name || null);
      setError(null);
    } catch (err) {
      console.error('Error loading features:', err);
      if (fuerNutzer.current !== id) return;
      setError(err);
      setFeatures({});
      setUsage({});
      setRole(null);
      setRoleName(null);
    } finally {
      if (fuerNutzer.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLaedt) return;
    if (nutzerId === null) {
      setFeatures({});
      setUsage({});
      setRole(null);
      setRoleName(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void loadFeatures();
  }, [authLaedt, nutzerId, loadFeatures]);

  // Prüft ob ein Feature aktiviert ist (boolean)
  const canUseFeature = useCallback((featureCode) => {
    const value = features[featureCode];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0; // -1 = unlimited, >0 = allowed
    return false;
  }, [features]);

  // Holt das Limit für ein Feature (integer)
  const getLimit = useCallback((featureCode) => {
    const value = features[featureCode];
    if (typeof value === 'number') return value;
    return 0;
  }, [features]);

  // Prüft ob ein Limit erreicht ist
  const isLimitReached = useCallback((featureCode) => {
    const usageData = usage[featureCode.replace('max_', '')];
    if (!usageData) return false;
    if (usageData.unlimited) return false;
    return usageData.current >= usageData.limit;
  }, [usage]);

  // Holt Usage-Daten für ein Feature
  const getUsage = useCallback((featureCode) => {
    // Konvertiere feature code zu usage key (z.B. max_tables -> tables)
    const usageKey = featureCode.replace('max_', '').replace('_stored', '');
    return usage[usageKey] || { current: 0, limit: 0, unlimited: false };
  }, [usage]);

  // Formatiert das Limit für die Anzeige
  const formatLimit = useCallback((featureCode) => {
    const limit = getLimit(featureCode);
    if (limit === -1) return '∞';
    return limit.toString();
  }, [getLimit]);

  const value = {
    features,
    usage,
    role,
    roleName,
    loading,
    error,
    canUseFeature,
    getLimit,
    isLimitReached,
    getUsage,
    formatLimit,
    refresh: loadFeatures,
  };

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
}

/**
 * Hook zum Zugriff auf Features.
 *
 * **Ohne Provider gibt es kein Merkmal.** Der frühere Ersatz sagte zu jedem
 * `canUseFeature` ja und zu jedem Limit „unbegrenzt" — er hat den einen
 * Routen-Wrapper, dem der Provider fehlte (`AdminRoute`), monatelang
 * zugedeckt: die Verwaltungsnavigation zeigte „Rollen & Rechte" und den
 * „Gefahrenbereich" auch dem, der beides nicht darf, und der Nutzer erfuhr
 * erst vom Backend-403, dass er nicht durfte.
 *
 * Genau das meint Regel #7: ein Ersatz, der „funktioniert", maskiert
 * zuverlässig den echten Fehler. Fehlt der Provider, wird das jetzt laut —
 * einmal je Aufrufer, nicht bei jedem Rendern.
 */
const _gemeldet = new Set();

export function useFeatures() {
  const context = useContext(FeaturesContext);
  if (!context) {
    // Der Stapel nennt die Komponente, der der Provider fehlt — ohne ihn
    // sucht man in sechs Routen-Wrappern.
    const woher = new Error().stack?.split('\n')[2]?.trim() ?? 'unbekannt';
    if (!_gemeldet.has(woher)) {
      _gemeldet.add(woher);
      console.error(
        'useFeatures() ohne FeaturesProvider — kein Merkmal ist verfügbar. ' +
        'Die Komponente gehört unter einen Routen-Wrapper mit Provider. ' +
        'Aufrufer:', woher,
      );
    }
    return {
      features: {},
      usage: {},
      role: null,
      roleName: null,
      loading: false,
      error: new Error('FeaturesProvider fehlt'),
      // Nichts erlauben statt alles: eine zu wenig angezeigte Schaltfläche
      // fällt beim Hinsehen auf, eine zu viel erst beim Klicken.
      // Dieselbe Signatur wie im Provider — TypeScript liest sie aus diesem Zweig.
      canUseFeature: (featureCode) => false,
      getLimit: (featureCode) => 0,
      isLimitReached: () => true,
      getUsage: () => ({ current: 0, limit: 0, unlimited: false }),
      formatLimit: () => '0',
      refresh: () => {},
    };
  }
  return context;
}

export default useFeatures;
